import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';

import {
  TenantReadModel,
  useGetAgreementsStakeholdersQuery,
  useGetCompaniesAtBuildingAddressQuery,
  useLazyGetCompaniesByIdQuery,
  usePostCompaniesMutation,
} from '@client/api';
import { CompanySearchForm, CompanySearchFormData } from '../../../companies/components/company-search.form';
import {
  Button,
  Checkbox,
  EmptyPlaceholder,
  isDefinedString,
  ItemsListAssignment,
  Modal,
  ModalContent,
  ModalFooter,
  Pagination,
  SearchField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadIconCell,
  TableHeadRow,
  TableIconCell,
  TableRow,
  Text,
  useSearching,
} from '@client/toolkit';

type FilterTab = 'address' | 'all';

export interface PortfolioTenantSelectionModalProps {
  open: boolean;
  buildingId?: string;
  /**
   * Heading for the dialog. The occupancy register reuses this picker for operators and other
   * occupants, where "Velg leietaker" would mislabel the party being chosen.
   */
  title?: string;
  onClose: () => void;
  onSubmit: (items: TenantReadModel[]) => void;
}

export function PortfolioTenantSelectionModal({
  open,
  buildingId,
  title,
  onClose,
  onSubmit,
}: PortfolioTenantSelectionModalProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tCompanies } = useTranslation('companies');

  const [activeTab, setActiveTab] = useState<FilterTab>('address');
  const { search, debouncedSearch, updateSearch, clearSearch, hasSearch } = useSearching();
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [postCompany] = usePostCompaniesMutation();
  const [getCompanyById] = useLazyGetCompaniesByIdQuery();

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  // "Alle" data — paginated stakeholders search
  const {
    currentData: allCurrentData,
    data: allData,
    isFetching: isFetchingAll,
  } = useGetAgreementsStakeholdersQuery(
    {
      search: debouncedSearch ?? '',
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
    },
    { skip: activeTab !== 'all' },
  );

  // "På samme adresse" data
  const { data: companiesAtAddress, isFetching: isFetchingAddress } =
    useGetCompaniesAtBuildingAddressQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const addressCompanyIds = useMemo(
    () => new Set(companiesAtAddress?.map((c) => c.id).filter(Boolean) ?? []),
    [companiesAtAddress],
  );

  // Map address companies to TenantReadModel shape for consistent handling
  const addressTenants: TenantReadModel[] = useMemo(
    () =>
      (companiesAtAddress ?? [])
        .filter((c) => {
          if (!debouncedSearch) return true;
          const term = debouncedSearch.toLowerCase();
          return (
            c.name?.toLowerCase().includes(term) ||
            c.organizationNumber?.toLowerCase().includes(term)
          );
        })
        .map((c) => ({
          // `id` is the Aktør (stakeholder) id, `companyId` the company — two different
          // entities. Consumers writing a stakeholder FK (the occupancy register) need the
          // former; consumers storing a company (portfolio tenants) need the latter. Falling
          // back to the company id would reintroduce the mismatch, so leave it empty and let
          // the server reject it with a clear error instead of a foreign-key failure.
          id: c.stakeholderId ?? '',
          name: c.name ?? '',
          companyId: c.id,
          type: 'Company' as const,
        })),
    [companiesAtAddress, debouncedSearch],
  );

  const allItems = useMemo(
    () => allCurrentData?.items ?? (!open ? allData?.items : []) ?? [],
    [allCurrentData?.items, allData?.items, open],
  );
  const allTotalCount = allCurrentData?.totalCount || allData?.totalCount || 0;

  const searchedItems = activeTab === 'address' ? addressTenants : allItems;
  const totalCount = activeTab === 'address' ? addressTenants.length : allTotalCount;
  const isFetching = activeTab === 'address' ? isFetchingAddress : isFetchingAll;

  const [selectedItems, setSelectedItems] = useState<TenantReadModel[]>([]);

  const handleToggleItem = (item: TenantReadModel) => {
    const index = selectedItems.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      const next = [...selectedItems];
      next.splice(index, 1);
      setSelectedItems(next);
    } else {
      // Max 1 selection for portfolio tenant
      setSelectedItems([item]);
    }
  };

  const handleRemoveItem = (item: TenantReadModel) => {
    setSelectedItems((prev) => prev.filter((x) => x.id !== item.id));
  };

  const isItemAssigned = (item: TenantReadModel) =>
    selectedItems.findIndex((x) => x.id === item.id) >= 0;

  const handleChangeSearch = (value: string | null) => {
    setPage(0);
    updateSearch(value);
  };

  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
    setPage(0);
  };

  useEffect(() => {
    if (!open) return;
    setSelectedItems([]);
    setActiveTab('address');
    clearSearch();
    setPage(0);
    setShowCreateForm(false);
  }, [open]);

  const handleCreateCompany = async (data: CompanySearchFormData) => {
    const { numberOfEmployees, ...rest } = data;
    const result = await postCompany({
      body: { ...rest, numberOfEmployees: numberOfEmployees ? parseInt(numberOfEmployees, 10) : null },
    }).unwrap();

    if (result?.id) {
      // postCompanies returns LegalPersonReadModel (no stakeholderId). Fetch the full
      // CompanyReadModel to resolve the Aktør id, then auto-select it.
      const company = await getCompanyById({ id: result.id }).unwrap();
      if (company?.stakeholderId) {
        setSelectedItems([
          {
            id: company.stakeholderId,
            name: company.name ?? result.name ?? '',
            companyId: company.id,
            type: 'Company' as const,
          },
        ]);
      }
    }
    setShowCreateForm(false);
  };

  return (
    <Modal
      open={open}
      appear
      size="3xl"
      onClose={onClose}
      onBackdropClick={onClose}
      title={title ?? 'Velg leietaker'}
    >
      <ModalContent>
        <ItemsListAssignment
          selectedLabel={tCommon('selected')}
          isSubmitting={false}
          searchedItems={searchedItems}
          selectedItems={selectedItems}
          SearchedContainerHeader={
            <div className="flex items-center gap-2">
              <SearchField
                className="flex-1"
                value={search}
                onChange={handleChangeSearch}
                onClear={clearSearch}
                placeholder={tCommon('search')}
                progressing={isFetching && hasSearch}
              />
              <Button
                size="sm"
                className="whitespace-nowrap"
                color={activeTab === 'address' ? 'primary' : 'primary-dark'}
                onClick={() => handleTabChange('address')}
              >
                På samme adresse
              </Button>
              <Button
                size="sm"
                color={activeTab === 'all' ? 'primary' : 'primary-dark'}
                onClick={() => handleTabChange('all')}
              >
                Alle
              </Button>
              <Button
                size="sm"
                className="whitespace-nowrap"
                color="primary-dark"
                onClick={() => setShowCreateForm(true)}
              >
                {tCompanies('companyCreate.title')}
              </Button>
            </div>
          }
          SearchedContainerContent={(items) => (
            <Table size="xs" layout="fixed" className="relative w-full">
              <TableHead className="sticky top-0 z-50">
                <TableHeadRow>
                  <TableHeadIconCell className="w-[58px]" />
                  <TableHeadCell>{tGlobal('entity.legalPerson')}</TableHeadCell>
                </TableHeadRow>
              </TableHead>

              {items.length <= 0 && !isFetching ? (
                <caption className="px-4 py-4 [caption-side:bottom]">
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal('entity.legalPerson', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                </caption>
              ) : (
                <TableBody>
                  {isFetching &&
                    Array.from(Array(pageSize).keys()).map((x) => (
                      <TableRow key={x}>
                        <TableIconCell>
                          <Checkbox className="mr-2 ml-2" defaultChecked={false} disabled />
                        </TableIconCell>
                        <TableCell loading />
                      </TableRow>
                    ))}
                  {!isFetching &&
                    items.map((item: TenantReadModel, idx: number) => {
                      const isAtAddress = addressCompanyIds.has(item.companyId ?? item.id);
                      return (
                        <TableRow key={idx} onClick={() => handleToggleItem(item)} hover>
                          <TableIconCell>
                            <Checkbox className="mr-2 ml-2" checked={isItemAssigned(item)} readOnly />
                          </TableIconCell>
                          <TableCell className="text-ellipsis overflow-hidden">
                            <div className="flex items-center gap-2">
                              <Text
                                className="text-sm font-medium whitespace-nowrap"
                                isEmpty={!isDefinedString(item.name)}
                              >
                                {item.name}
                              </Text>
                              {isAtAddress && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-primary/20 text-brand-primary font-medium whitespace-nowrap">
                                  På adressen
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              )}
            </Table>
          )}
          SearchedContainerFooter={
            activeTab === 'all' ? (
              <Pagination
                className="flex-1"
                totalCount={totalCount}
                currentPage={page}
                setPage={setPage}
                pageSize={pageSize}
                visible
              />
            ) : undefined
          }
          SelectedContainerContent={(items) => (
            <>
              {items.map((selectedItem, idx) => (
                <div
                  key={idx}
                  className="flex items-center w-full text-left text-white rounded-lg bg-surface-selected px-5 py-3 shadow-md"
                >
                  <div className="flex-1 overflow-hidden text-ellipsis">
                    <Text
                      as="div"
                      className="text-sm font-medium self-start whitespace-nowrap"
                      isEmpty={!isDefinedString(selectedItem.name)}
                    >
                      {selectedItem.name}
                    </Text>
                  </div>
                  <Button size="xs" color="primary-invert" onClick={() => handleRemoveItem(selectedItem)}>
                    <FaRegTrashAlt />
                  </Button>
                </div>
              ))}
            </>
          )}
        />
      </ModalContent>

      <ModalFooter>
        <Button
          disabled={selectedItems.length === 0 || isFetching}
          onClick={() => onSubmit(selectedItems)}
        >
          {tCommon('save')}
        </Button>
        <Button color="primary-dark" onClick={onClose}>
          {tCommon('close')}
        </Button>
      </ModalFooter>

      {showCreateForm && (
        <CompanySearchForm
          open={showCreateForm}
          title={tCompanies('companyCreate.title')}
          onSubmit={handleCreateCompany}
          onClose={() => setShowCreateForm(false)}
        />
      )}
    </Modal>
  );
}
